import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Empty, Input, message, Modal, Pagination, Space, Spin, Typography } from 'antd';
import { CloudUploadOutlined, ReloadOutlined, RobotOutlined } from '@ant-design/icons';
import ErrorBoundary from '../../components/ErrorBoundary';
import UploadItem from '../../components/UploadItem';
import WebHostingAiModal from '../../components/WebHostingAiModal';
import { getId, uploadHtmlContent } from '../../helpers/file';
import { useReuploadPolicy } from '../../hooks/useReuploadPolicy';
import { useTranslation } from '../../hooks/useTranslation';
import { WebHostingAiCapability, getWebHostingAiCapability } from '../../services/webHostingAi';
import { buildDefaultHtmlFileName, isValidHtmlFileName } from '../../utils/htmlFileName';
import { showUploadErrorModal } from '../../utils/uploadErrorModal';
import './index.css';

interface UploadRecord {
  id: string;
  dir?: string;
  url: string;
  rawUrl?: string;
  contentType?: string;
  customDomain?: string | null;
  fileName: string;
  time: string | number;
  size?: string;
  illicit?: number;
  illicitDetails?: string;
  storageType?: string;
  expireTime?: string | null;
  htmlShareEnabled?: boolean;
}

export { buildDefaultHtmlFileName, isValidHtmlFileName };

const GOOGLE_FONTS_CSS2_URL = 'https://fonts.googleapis.com/css2';

const WebHosting: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { policy: reuploadPolicy } = useReuploadPolicy();
  const tRef = useRef(t);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState(buildDefaultHtmlFileName);
  const [htmlContent, setHtmlContent] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importingHtmlFile, setImportingHtmlFile] = useState(false);
  const [draggingHtmlFile, setDraggingHtmlFile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<UploadRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [keyword, setKeyword] = useState('');
  const [searchText, setSearchText] = useState('');
  const [lastUploadUrl, setLastUploadUrl] = useState('');
  const [userInfo, setUserInfo] = useState<Record<string, any>>({});
  const [aiCapability, setAiCapability] = useState<WebHostingAiCapability>();
  const [aiModalOpen, setAiModalOpen] = useState(false);

  const isVip = Boolean(userInfo?.detail?.isActiveVip || userInfo?.isActiveVip);

  const trimmedFileName = fileName.trim();
  const canUpload = useMemo(() => {
    return htmlContent.trim().length > 0 && isValidHtmlFileName(trimmedFileName) && !uploading && !importingHtmlFile;
  }, [htmlContent, trimmedFileName, uploading, importingHtmlFile]);
  const hasGoogleFontsResource = useMemo(() => {
    return htmlContent.includes(GOOGLE_FONTS_CSS2_URL);
  }, [htmlContent]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const loadHistory = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({
      uid: getId() || '',
      page: String(current),
      pageSize: String(pageSize),
      fileType: 'html',
    });
    if (keyword.trim()) {
      params.set('keyword', keyword.trim());
    }

    fetch(`/img/history?${params.toString()}`)
      .then(res => res.json())
      .then(pageData => {
        setList(pageData.data || []);
        setTotal(pageData.total || 0);
      })
      .catch(error => {
        console.error('加载网页托管列表失败:', error);
        message.error(tRef.current('webHosting.listLoadFailed'));
      })
      .finally(() => setLoading(false));
  }, [current, pageSize, keyword]);

  useEffect(() => {
    fetch('/login/info', { method: 'GET' })
      .then(res => res.json())
      .then(data => {
        if (!data.isSuccess) {
          return;
        }
        const info = data.detail || data;
        if (info.config && typeof info.config === 'string') {
          try {
            info.config = JSON.parse(info.config);
          } catch (error) {
            console.error('解析用户配置失败:', error);
          }
        }
        setUserInfo(info);
      })
      .catch(error => {
        console.error('获取用户信息失败:', error);
      });
  }, []);

  useEffect(() => {
    getWebHostingAiCapability()
      .then(setAiCapability)
      .catch(() => setAiCapability(undefined));
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleUpload = async () => {
    if (!htmlContent.trim()) {
      message.warning(t('webHosting.emptyContent'));
      return;
    }
    if (!isValidHtmlFileName(trimmedFileName)) {
      message.warning(t('webHosting.invalidFileName'));
      return;
    }

    setUploading(true);
    try {
      const record = await uploadHtmlContent(
        trimmedFileName,
        htmlContent,
        (percent) => {
          let content = t('upload.uploadProgress', { percent });
          if (percent === 100) {
            content = t('upload.uploadComplete');
          }
          message.info({ key: 'webHostingUpload', duration: 1000, content });
        },
        (errorMessage, failedFileName, fileSize, httpStatus) => {
          showUploadErrorModal(t, errorMessage, failedFileName, undefined, fileSize, httpStatus);
        }
      );

      message.success({ key: 'webHostingUpload', content: t('webHosting.uploadSuccess') });
      setLastUploadUrl(record?.url || '');
      setUploadModalOpen(false);
      setFileName(buildDefaultHtmlFileName());
      setHtmlContent('');
      setCurrent(1);
      if (current === 1) {
        loadHistory();
      }
    } catch (error) {
      console.error('上传 HTML 失败:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleSearch = (value: string) => {
    setKeyword(value.trim());
    setCurrent(1);
  };

  const handleOpenUploadModal = () => {
    if (!htmlContent.trim()) {
      setFileName(buildDefaultHtmlFileName());
    }
    setUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    if (!uploading && !importingHtmlFile) {
      setUploadModalOpen(false);
    }
  };

  const readFileAsText = (file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.onerror = () => reject(reader.error || new Error('read file failed'));
      reader.readAsText(file, 'UTF-8');
    });
  };

  const importHtmlFile = async (file: File) => {
    setImportingHtmlFile(true);
    try {
      const content = await readFileAsText(file);
      setFileName(file.name.trim());
      setHtmlContent(content);
      message.success(t('webHosting.importFileSuccess', { fileName: file.name }));
    } catch (error) {
      console.error('读取 HTML 文件失败:', error);
      message.error(t('webHosting.importFileReadFailed'));
    } finally {
      setImportingHtmlFile(false);
    }
  };

  const importFirstHtmlFile = (files: File[]) => {
    if (uploading || importingHtmlFile || files.length === 0) {
      return;
    }

    const htmlFile = files.find((file) => isValidHtmlFileName(file.name));
    if (!htmlFile) {
      message.warning(t('webHosting.invalidImportFile'));
      return;
    }

    if (files.length > 1) {
      message.info(t('webHosting.extraFilesIgnored'));
    }

    importHtmlFile(htmlFile);
  };

  const openHtmlFilePicker = () => {
    if (uploading || importingHtmlFile) {
      return;
    }
    importInputRef.current?.click();
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    importFirstHtmlFile(Array.from(event.target.files || []));
    event.target.value = '';
  };

  const handleImportDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!uploading && !importingHtmlFile) {
      setDraggingHtmlFile(true);
    }
  };

  const handleImportDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
      setDraggingHtmlFile(false);
    }
  };

  const handleImportDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDraggingHtmlFile(false);
    importFirstHtmlFile(Array.from(event.dataTransfer.files || []));
  };

  const handleImportKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openHtmlFilePicker();
    }
  };

  const isNativeTextPasteTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) {
      return false;
    }
    return target.tagName === 'INPUT'
      || target.tagName === 'TEXTAREA'
      || target.isContentEditable
      || Boolean(target.closest('[contenteditable="true"]'));
  };

  const getClipboardFiles = (clipboardData: DataTransfer) => {
    const files = Array.from(clipboardData.files || []);
    if (files.length > 0) {
      return files;
    }

    return Array.from(clipboardData.items || [])
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter((file): file is File => Boolean(file));
  };

  const handleModalPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    if (uploading || importingHtmlFile) {
      return;
    }

    const files = getClipboardFiles(event.clipboardData);
    if (files.length > 0) {
      event.preventDefault();
      importFirstHtmlFile(files);
      return;
    }

    if (isNativeTextPasteTarget(event.target)) {
      return;
    }

    const text = event.clipboardData.getData('text/plain') || event.clipboardData.getData('text/html');
    if (text.trim()) {
      event.preventDefault();
      setHtmlContent(text);
      message.success(t('webHosting.pasteImportSuccess'));
    }
  };

  return (
    <div className="web-hosting-page">
      <Alert
        message={t('webHosting.subtitle')}
        type="info"
        showIcon
        style={{ marginBottom: '12px' }}
      />

      <div className="web-hosting-list-toolbar">
        <Input.Search
          allowClear
          className="web-hosting-search"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          onSearch={handleSearch}
          placeholder={t('webHosting.searchPlaceholder')}
        />
        <Space className="web-hosting-toolbar-actions" wrap>
          <Button icon={<ReloadOutlined />} onClick={loadHistory} loading={loading}>
            {t('webHosting.refreshList')}
          </Button>
          {aiCapability?.enabled && (
            <Button
              icon={<RobotOutlined />}
              onClick={() => setAiModalOpen(true)}
            >
              {t('webHostingAi.entry')}
            </Button>
          )}
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={handleOpenUploadModal}
          >
            {t('webHosting.uploadEntry')}
          </Button>
        </Space>
      </div>

      {lastUploadUrl && (
        <Alert
          className="web-hosting-latest-alert"
          style={{ marginBottom: 12 }}
          message={t('webHosting.latestUpload')}
          description={<a href={lastUploadUrl} target="_blank" rel="noopener noreferrer">{lastUploadUrl}</a>}
          type="success"
          showIcon
        />
      )}

      <Spin spinning={loading}>
        {list.length === 0 ? (
          <Empty description={t('webHosting.emptyList')} />
        ) : (
          <div className="list web-hosting-list">
            {list.map((item, index) => (
              <ErrorBoundary key={item.id}>
                <UploadItem
                  dir={item.dir}
                  index={index}
                  userInfo={userInfo}
                  url={item.url}
                  rawUrl={item.rawUrl}
                  contentType={item.contentType}
                  customDomain={item.customDomain || ''}
                  id={item.id}
                  fileName={item.fileName}
                  time={item.time}
                  size={item.size}
                  illicit={item.illicit}
                  illicitDetails={item.illicitDetails}
                  storageType={item.storageType}
                  expireTime={item.expireTime}
                  htmlShareEnabled={item.htmlShareEnabled}
                  onDelete={loadHistory}
                  onRefresh={loadHistory}
                  selected={false}
                  onSelect={() => undefined}
                  selectedIds={[]}
                  reuploadPolicy={reuploadPolicy}
                  selectable={false}
                />
              </ErrorBoundary>
            ))}
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
          </div>
        )}
      </Spin>

      <div className="web-hosting-pagination">
        <Pagination
          current={current}
          size="small"
          showSizeChanger
          pageSizeOptions={['10', '20', '50', '100']}
          total={total}
          showTotal={(nextTotal) => t('common.totalRecords', { total: nextTotal })}
          pageSize={pageSize}
          onChange={(page, nextPageSize) => {
            setCurrent(page);
            setPageSize(nextPageSize);
          }}
        />
      </div>

      <Modal
        title={t('webHosting.uploadModalTitle')}
        open={uploadModalOpen}
        width={760}
        getContainer={false}
        maskClosable={!uploading && !importingHtmlFile}
        keyboard={!uploading && !importingHtmlFile}
        onCancel={handleCloseUploadModal}
        footer={[
          <Button key="cancel" onClick={handleCloseUploadModal} disabled={uploading || importingHtmlFile}>
            {t('common.cancel')}
          </Button>,
          <Button
            key="upload"
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={uploading}
            disabled={!canUpload}
            onClick={handleUpload}
          >
            {t('webHosting.uploadButton')}
          </Button>,
        ]}
      >
        <Space
          className="web-hosting-upload-modal-content"
          direction="vertical"
          size={14}
          style={{ width: '100%' }}
          onPaste={handleModalPaste}
        >
          <Alert message={t('webHosting.uploadNotice')} type="info" showIcon />
          <Input
            value={fileName}
            onChange={(event) => setFileName(event.target.value)}
            addonBefore={t('webHosting.fileName')}
            status={trimmedFileName && !isValidHtmlFileName(trimmedFileName) ? 'error' : undefined}
            placeholder="page.html"
          />
          <div
            className={[
              'web-hosting-import-zone',
              draggingHtmlFile ? 'web-hosting-import-zone-active' : '',
              uploading || importingHtmlFile ? 'web-hosting-import-zone-disabled' : '',
            ].filter(Boolean).join(' ')}
            role="button"
            tabIndex={uploading || importingHtmlFile ? -1 : 0}
            aria-disabled={uploading || importingHtmlFile}
            data-testid="web-hosting-import-zone"
            onClick={openHtmlFilePicker}
            onKeyDown={handleImportKeyDown}
            onDragOver={handleImportDragOver}
            onDragLeave={handleImportDragLeave}
            onDrop={handleImportDrop}
          >
            <input
              ref={importInputRef}
              className="web-hosting-file-input"
              data-testid="web-hosting-file-input"
              type="file"
              accept=".html,.htm,text/html"
              multiple
              disabled={uploading || importingHtmlFile}
              onChange={handleFileInputChange}
            />
            <CloudUploadOutlined className="web-hosting-import-icon" />
            <div className="web-hosting-import-copy">
              <Typography.Text strong>{t('webHosting.importDropTitle')}</Typography.Text>
              <Typography.Text type="secondary">{t('webHosting.importDropHint')}</Typography.Text>
              {importingHtmlFile && (
                <Typography.Text type="secondary">{t('webHosting.importingFile')}</Typography.Text>
              )}
            </div>
          </div>
          <Input.TextArea
            value={htmlContent}
            onChange={(event) => setHtmlContent(event.target.value)}
            rows={14}
            placeholder={t('webHosting.htmlPlaceholder')}
            className="web-hosting-editor"
          />
          {hasGoogleFontsResource && (
            <Alert
              className="web-hosting-google-fonts-alert"
              message={t('webHosting.googleFontsWarningTitle')}
              description={t('webHosting.googleFontsWarningDescription')}
              type="warning"
              showIcon
            />
          )}
          <div className="web-hosting-upload-actions">
            <Typography.Text type="secondary">
              {t('webHosting.contentSize', { count: new Blob([htmlContent]).size })}
            </Typography.Text>
            <Button onClick={() => setFileName(buildDefaultHtmlFileName())}>
              {t('webHosting.resetFileName')}
            </Button>
          </div>
        </Space>
      </Modal>

      {aiCapability?.enabled && (
        <WebHostingAiModal
          open={aiModalOpen}
          isVip={isVip}
          capability={aiCapability}
          onCapabilityChange={setAiCapability}
          onCancel={() => setAiModalOpen(false)}
          onGenerated={() => {
            setAiModalOpen(false);
            navigate('/web-hosting/ai-preview');
          }}
        />
      )}
    </div>
  );
};

export default WebHosting;
